import {
  GatewayConformiteRcPro,
  GatewayContact,
  GatewayEntreprise,
  GatewayInfoSocieteOrias,
} from "@conformite/gateway";
import {
  Button,
  displayErrorToast,
  Link,
  MaxWidthContent,
  SkeletonCard,
} from "@endya-conformite/ui-kit";
import { GatewayRcProApi } from "@src/api/conformite/rcPro.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import ChevronLeft from "@endya-conformite/ui-kit/src/assets/icons/chevron-left.svg?react";
import { MainContentPageTemplate } from "@src/components/MainPageTemplate/MainContentPageTemplate";
import {
  useHasForbiddenTypeAdhesion,
  useIsAllowed,
} from "@src/session/useIsAllowed";
import { categorieToAvancementKey } from "@src/societes/societe.helper";
import { StoreEntryFetchStatus } from "@src/store/store";
import { useEntreprises } from "@src/store/store.entreprise";
import { useMillesime } from "@src/store/store.millesime";
import { merge } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { isCategorie, RcProPropertyDefault } from "./conformiteRcPro.helper";
import styles from "./ConformiteRcPro.module.scss";
import { ConformiteRcProEdit } from "./edit/ConformiteRcProEdit";
import { ConformiteRcProRead } from "./read/ConformiteRcProRead";

type UseRcPro = {
  id?: string;
  millesime?: number;
  categorie?: string;
  isAdmin?: boolean;
};
export const useRcPro = ({
  id,
  millesime,
  categorie,
  isAdmin = false,
}: UseRcPro) => {
  const [rcPro, setRcPro] =
    useState<GatewayConformiteRcPro.ConformiteRcProProperty | null>(null);
  const retrieveRcPro = useCallback(() => {
    if (!id || !millesime || !categorie || !isCategorie(categorie)) return;
    GatewayRcProApi.getRcPro(id, millesime, categorie, isAdmin)
      .then((data) => {
        setRcPro(data);
      })
      .catch((error) => {
        handleGatewayError({
          onUnhandled: () => {
            displayErrorToast(
              "Une erreur est survenue lors de la récupération du formulaire"
            );
          },
        })(error);
      });
  }, [id, millesime, categorie, isAdmin]);

  useEffect(() => {
    retrieveRcPro();
  }, [id, categorie, millesime, retrieveRcPro]);
  const properties = useMemo(() => {
    const defaultProperties =
      RcProPropertyDefault[categorie as GatewayInfoSocieteOrias.Categorie];
    return merge({ ...defaultProperties }, rcPro);
  }, [rcPro, categorie]);
  return {
    rcPro,
    properties,
    retrieveRcPro,
  };
};
export function ConformiteRcProContainer() {
  const hasForbiddenTypeAdhesion = useHasForbiddenTypeAdhesion();
  const { id, categorie: categorieLowercase } = useParams<{
    id: string;
    categorie: string;
  }>();
  const [query] = useSearchParams();
  const fromAxe = query.get("fromAxe");
  const { selected: millesime } = useMillesime();
  const { entreprisesBySiren, updateAvancement, status } = useEntreprises();
  const [isEditing, setIsEditing] = useState(false);
  const { search } = useLocation();
  const societe = id && entreprisesBySiren ? entreprisesBySiren[id] : undefined;
  const categorie = categorieLowercase?.toUpperCase();
  const { hasAllowedRole } = useIsAllowed();

  const { rcPro, properties, retrieveRcPro } = useRcPro({
    id,
    millesime,
    categorie,
  });

  if (!id) {
    return <Navigate to="/societes" />;
  }

  if (!categorie || !isCategorie(categorie)) {
    return <Navigate to={`/societes/${id}${search}`} />;
  }
  const isConforme =
    societe?.avancement[categorieToAvancementKey[categorie]] === 100;

  return (
    <MainContentPageTemplate
      title={
        <div className={styles.title}>
          <h1>
            RC Pro
            {` ${categorieLowercase?.charAt(0).toUpperCase() ?? ""}${
              categorieLowercase?.slice(1).toUpperCase() ?? ""
            }`}
          </h1>
          <h2>{societe?.raisonSociale}</h2>
        </div>
      }
      hasMillesimeNavigation={hasAllowedRole(
        GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR,
        GatewayContact.ContactRoleUtilisateur.RESPONSABLE_CONFORMITE
      )}
      actionNavigation={
        hasForbiddenTypeAdhesion([
          GatewayEntreprise.TypeAdhesion.SOCIETE_INDIVIDUELLE,
        ]) ? null : (
          <Link
            to={
              fromAxe === null
                ? `/societes/${id}`
                : `/conformite/rc-pro/${categorieLowercase || ""}`
            }
          >
            <Button variant="text" icon={<ChevronLeft />}>
              Retour
            </Button>
          </Link>
        )
      }
      millesime={millesime}
      noEntity={!societe && status === StoreEntryFetchStatus.Loaded}
    >
      {status === StoreEntryFetchStatus.Loading && (
        <MaxWidthContent size="large">
          <SkeletonCard height={500} />
        </MaxWidthContent>
      )}
      {millesime && societe && !isEditing && (
        <ConformiteRcProRead
          properties={properties}
          isConforme={isConforme}
          millesime={millesime}
          setToEdit={() => setIsEditing(true)}
          siren={societe.siren}
          categorie={categorie}
        />
      )}
      {millesime && societe && isEditing && (
        <ConformiteRcProEdit
          siren={id}
          societe={societe}
          millesime={millesime}
          isConforme={isConforme}
          categorie={categorie}
          rcPro={rcPro}
          stopEditing={(value, avancement) => {
            setIsEditing(false);
            if (value) retrieveRcPro();
            if (avancement) updateAvancement(avancement);
          }}
        />
      )}
    </MainContentPageTemplate>
  );
}
